import {
	Fragment,
	ReactElement,
	ReactNode,
	useCallback,
	useEffect,
	useMemo,
	useState
} from "react";
import classNames from "classnames";
import { WeekViewCard } from "./WeekViewCard";
import { DateString } from "../DateString/DateString";
import { Button } from "../Button/Button";
import {
	useCurrentHour,
	useForceRerender,
	WeekDay
} from "client/Hooks";
import { localize } from "client_legacy/ndaw/culture";
import { WeekViewContext } from "./WeekViewContext";
import {
	MINUTES_PER_HOUR,
	useWeekViewLayout,
	WeekViewAllDayItem,
	WeekViewDayLayout,
	WeekViewHourRange,
	WeekViewItemKey,
	WeekViewLayout
} from "./useWeekViewLayout";
import styles from "./WeekView.module.less";
import { clamp } from "client/Misc/utils";
import { TimeZone } from "client/DateAndTime/timezones";
import {
	addTime,
	calculateTimeDifference,
	millisecondsInAMinute,
	millisecondsInAnHour
} from "client/DateAndTime/dateTimeUtils";
import { convertToDateTime } from "client/DateAndTime/timeUtils";
import { TimeOnly } from "client/DateAndTime/timeOnly";

export type WeekViewMode = "stacked" | "hours";

export type WeekViewProps<TItem> = {
	mode?: WeekViewMode;
	days: readonly WeekDay[];
	items: readonly TItem[];
	itemKey: WeekViewItemKey<TItem>;
	startDateTimeRetriever: (item: TItem) => string | Date;
	endDateTimeRetriever: (item: TItem) => string | Date;
	timezone: TimeZone;
	children: (item: TItem) => ReactNode;
};

export const WeekView = <TItem,>(props: WeekViewProps<TItem>): ReactElement => {
	const {
		mode = "stacked",
		days,
		items,
		itemKey,
		startDateTimeRetriever,
		endDateTimeRetriever,
		timezone,
		children
	} = props;

	const layout: WeekViewLayout<TItem> = useWeekViewLayout(
		items,
		days,
		itemKey,
		startDateTimeRetriever,
		endDateTimeRetriever,
		timezone
	);

	return (
		<div
			className={classNames(
				styles.root,
				{
					[styles.root_withHourTrack]: mode === "hours",
					[styles.root_shouldFillHeight]: mode === "stacked"
				}
			)}
			style={{
				"--week-view-day-count": days.length
			}}
		>
			<WeekViewHeader
				days={days}
				allDayItems={layout.allDayItems}
				hasHourTrack={mode === "hours"}
				renderItem={children}
			/>

			{ mode === "hours"
				? (
					<WeekViewHours
						days={layout.days}
						hourRange={layout.hourRange}
						timezone={timezone}
						renderItem={children}
					/>
				)
				: (
					<WeekViewStacked
						days={layout.days}
						renderItem={children}
					/>
				)}
		</div>
	);
};
WeekView.displayName = "WeekView";

type WeekViewHeaderProps<TItem> = {
	days: readonly WeekDay[];
	allDayItems: readonly WeekViewAllDayItem<TItem>[];
	hasHourTrack: boolean;
	renderItem: (item: TItem) => ReactNode;
};

const WeekViewHeader = <TItem,>(props: WeekViewHeaderProps<TItem>): ReactElement => {
	const {
		days,
		allDayItems,
		hasHourTrack,
		renderItem
	} = props;

	const [isAllDaySectionExpanded, setIsAllDaySectionExpanded] = useState<boolean>(false);

	const toggleAllDaySection = useCallback(
		(): void => {
			setIsAllDaySectionExpanded((x) => !x);
		},
		[]
	);

	const visibleItems: readonly WeekViewAllDayItem<TItem>[] = isAllDaySectionExpanded
		? allDayItems
		: allDayItems.filter((x) => x.row < COLLAPSED_ALL_DAY_ROW_COUNT);

	const hiddenItemCountPerDay: readonly number[] = days.map(
		(_, i): number => countHiddenAllDayItems(allDayItems, i)
	);

	const visibleRowCount: number = Math.max(
		...visibleItems.map((x) => x.row + 1),
		0
	);

	const hasHiddenItems: boolean = hiddenItemCountPerDay.some((x) => x > 0);

	const rowCount: number = 1 + visibleRowCount + (hasHiddenItems ? 1 : 0);

	const daySpan: string = `1 / span ${rowCount}`;

	const firstDayColumn: number = hasHourTrack
		? 2
		: 1;

	return (
		<div className={styles.header}>
			{ hasHourTrack && (
				<div
					className={styles.headerDay}
					style={{
						gridColumn: 1,
						gridRow: daySpan
					}}
				/>
			)}

			{ days.map((x, i) => (
				<div
					key={x.date.toISOString()}
					className={classNames(
						styles.headerDay,
						{
							[styles.headerDay_today]: x.isToday
						}
					)}
					style={{
						gridColumn: firstDayColumn + i,
						gridRow: daySpan
					}}
				>
					<p className={styles.headerDayName}>{x.name} </p>
					<h4>{x.dayInTheMonth}</h4>
				</div>
			))}

			<WeekViewContext.Provider value="all-day">
				{ visibleItems.map((x) => (
					<div
						key={x.key}
						className={styles.allDayItem}
						style={{
							gridColumn: `${firstDayColumn + x.firstDayIndex} / span ${x.lastDayIndex - x.firstDayIndex + 1}`,
							gridRow: x.row + 2
						}}
					>
						{renderItem(x.item)}
					</div>
				))}
			</WeekViewContext.Provider>

			{ days.map((x, i) => hiddenItemCountPerDay[i] > 0 && (
				<div
					key={x.date.toISOString()}
					className={styles.allDayMore}
					style={{
						gridColumn: firstDayColumn + i,
						gridRow: rowCount
					}}
				>
					<Button
						kind="transparent-for-list"
						text={isAllDaySectionExpanded
							? localize("_ViewLess")
							: {
								template: localize("_XMore"),
								replacements: [
									{
										what: "{{Number}}",
										filler: String(hiddenItemCountPerDay[i])
									}
								]
							}
						}
						icon={isAllDaySectionExpanded
							? "chevron-up"
							: "chevron-down"
						}
						onClick={toggleAllDaySection}
						bubbleClick={false}
					/>
				</div>
			))}
		</div>
	);
};
WeekViewHeader.displayName = "WeekViewHeader";

const COLLAPSED_ALL_DAY_ROW_COUNT = 2;

function countHiddenAllDayItems<TItem>(
	items: readonly WeekViewAllDayItem<TItem>[],
	dayIndex: number
): number {
	return items
		.filter(
			(x) => x.row >= COLLAPSED_ALL_DAY_ROW_COUNT
				&& x.firstDayIndex <= dayIndex
				&& x.lastDayIndex >= dayIndex
		)
		.length;
}

type WeekViewStackedProps<TItem> = {
	days: readonly WeekViewDayLayout<TItem>[];
	renderItem: (item: TItem) => ReactNode;
};

const WeekViewStacked = <TItem,>(props: WeekViewStackedProps<TItem>): ReactElement => {
	const {
		days,
		renderItem
	} = props;

	return (
		<div className={styles.body}>
			{ days.map((x) => (
				<div
					key={x.day.date.toISOString()}
					className={styles.content}
				>
					{ x.timedItems.map((x) => (
						<Fragment key={x.key}>
							{renderItem(x.item)}
						</Fragment>
					))}
				</div>
			))}
		</div>
	);
};
WeekViewStacked.displayName = "WeekViewStacked";

type WeekViewHoursProps<TItem> = {
	days: readonly WeekViewDayLayout<TItem>[];
	hourRange: WeekViewHourRange | undefined;
	timezone: TimeZone;
	renderItem: (item: TItem) => ReactNode;
};

const WeekViewHours = <TItem,>(props: WeekViewHoursProps<TItem>): ReactElement | null => {
	const {
		days,
		hourRange,
		timezone,
		renderItem
	} = props;

	if (hourRange === undefined) {
		return null;
	}

	const { startHour, endHour } = hourRange;

	const startTimeInMinutes: number = startHour * MINUTES_PER_HOUR;

	return (
		<div className={styles.body}>
			<WeekViewTimeIndicator
				start={startHour}
				end={endHour}
				timezone={timezone}
			/>
			<WeekViewHourTrack
				start={startHour}
				end={endHour}
				timezone={timezone}
			/>

			<WeekViewContext.Provider value="timed">
				{ days.map((x) => (
					<div
						key={x.day.date.toISOString()}
						className={classNames(styles.content, styles.content_withHourLines)}
					>
						{ x.timedItems.map((x) => (
							<div
								key={x.key}
								className={styles.timeBlock}
								style={{
									"--week-view-time-block-start": x.startTimeInMinutes - startTimeInMinutes,
									"--week-view-time-block-end": x.endTimeInMinutes - startTimeInMinutes,
									"--week-view-time-block-column": x.column,
									"--week-view-time-block-column-span": x.columnSpan,
									"--week-view-time-block-column-count": x.columnCount
								}}
							>
								{renderItem(x.item)}
							</div>
						))}
					</div>
				))}
			</WeekViewContext.Provider>
		</div>
	);
};
WeekViewHours.displayName = "WeekViewHours";

type WeekViewHourTrackProps = {
	start: number;
	end: number;
	timezone: TimeZone;
};

const WeekViewHourTrack = (props: WeekViewHourTrackProps): ReactElement => {
	const {
		start,
		end,
		timezone
	} = props;

	const currentHour: number = useCurrentHour(timezone);

	const blocks = useMemo(
		(): readonly HourBlock[] => {
			const startTime: Date = convertToDateTime(
				new TimeOnly(start, 0, 0, 0),
				timezone
			);

			const startTimeMs: number = startTime.getTime();

			const result: HourBlock[] = [];

			for (let i = start; i <= end; i++) {
				const date = new Date(startTimeMs + ((i - start) * millisecondsInAnHour));

				result.push({
					date,
					hour: i
				});
			}

			return result;
		},
		[
			start,
			timezone,
			end
		]
	);

	return (
		<div
			className={classNames(
				styles.content,
				styles.content_withHourLines,
				styles.content_hourTrack
			)}
		>
			{ blocks.map((x) => (
				<div
					key={x.date.toISOString()}
					className={classNames(
						styles.hourTrackBlock,
						{ [styles.hourTrackBlock_current]: x.hour === currentHour }
					)}
				>
					<DateString
						value={x.date}
						format="T"
						timezone={timezone}
					/>
				</div>
			))}
		</div>
	);
};
WeekViewHourTrack.displayName = "WeekViewHourTrack";

type HourBlock = {
	date: Date;
	hour: number;
};

type WeekViewTimeIndicatorProps = {
	start: number;
	end: number;
	timezone: TimeZone;
};

const WeekViewTimeIndicator = (props: WeekViewTimeIndicatorProps): ReactElement => {
	const {
		start,
		end,
		timezone
	} = props;

	const [rerender] = useForceRerender();

	const now = new Date();

	const startTime: Date = convertToDateTime(
		new TimeOnly(start, 0, 0, 0),
		timezone,
		now
	);

	const endTime: Date = convertToDateTime(
		new TimeOnly(end, 0, 0, 0),
		timezone,
		now
	);

	let msTillTopOfTheMinute: number = millisecondsInAMinute - (now.getTime() % millisecondsInAMinute);

	if (msTillTopOfTheMinute === 0) {
		msTillTopOfTheMinute = millisecondsInAMinute;
	}

	const timeoutHandle: SetTimeoutHandle = setTimeout(rerender, msTillTopOfTheMinute);

	useEffect(
		() => {
			return () => clearTimeout(timeoutHandle);
		},
		[timeoutHandle]
	);

	const minutesSinceStart: number = calculateTimeDifference(
		now,
		startTime,
		"minute"
	);

	const timeInMinutes: number = clamp(
		minutesSinceStart,
		0,
		calculateTimeDifference(
			addTime(
				endTime,
				1,
				"hour"
			),
			startTime,
			"minute"
		)
			- 1
	);

	return (
		<div className={styles.timeIndicatorHost}>
			<div
				className={styles.timeIndicator}
				style={{
					"--week-view-time-indicator-time": timeInMinutes
				}}
			/>
		</div>
	);
};
WeekViewTimeIndicator.displayName = "WeekViewTimeIndicator";

WeekView.Card = WeekViewCard;
